import type { PayloadAction } from '@reduxjs/toolkit';
import { createAction, createSlice } from '@reduxjs/toolkit';

import { BetOverrider } from '../../../controllers/betOverrider/BetOverrider';
import { CoreSessionStorage } from '../../../storage/session/CoreSessionStorage';
import {
  acceptedBetAlreadyExists,
  areBetsEqual,
  countTotalBet,
  doubleBets,
  emptyBets,
  getNewBet,
  isFreeBetPlaced,
} from '../../../utils/bets';
import { ChipTypes } from '../chips/types';

import type { AcceptedBet, BetPayloadType, BetsSliceType, BetType, IBetLimits, ILimit } from './types';

const initialState: BetsSliceType = {
  betList: null,
  betStack: [],
  disabledBetSpots: [],
  acceptedBets: [],
  lastBet: null,
  betLimits: null,
  tableLimits: null,
  dragBuffer: null,
  updateTimestamp: 0,
  rawTotalBet: 0,
  totalBet: 0,
  placedBetType: '',
  rebetAttempts: 0,
  multiplierFactor: null,
};

export const betSlice = createSlice({
  name: 'bets',
  initialState,
  reducers: {
    initBetStack: (state, action: PayloadAction<number[]>) => {
      state.betList = action.payload;
    },
    resetBetStack: (state) => {
      state.betStack = [];
    },
    setBetLimits: (state, action: PayloadAction<IBetLimits>) => {
      state.betLimits = action.payload;
    },
    setTableLimits: (state, action: PayloadAction<ILimit>) => {
      state.tableLimits = action.payload;
    },
    updateBetStackHead: (state, action: PayloadAction<BetType>) => {
      const betStack = state.betStack;
      const betStackHead = state.betStack[betStack.length - 1] ?? { betSpots: {} };
      const bet = action.payload;
      betStackHead.betSpots = Object.assign(betStackHead.betSpots, bet.betSpots);

      if (betStack.length > 0) {
        state.betStack[betStack.length - 1] = betStackHead;
      } else {
        state.betStack.push(betStackHead);
      }
    },
    overrideBetStack: (state, action: PayloadAction<BetType[]>) => {
      state.betStack = action.payload;
    },
    pushBet: (state, action: PayloadAction<BetPayloadType>) => {
      if (!state.betList) return;

      const { bet: changedBets, decimals, freeChipValue, freeChipBalance, balance } = action.payload;
      const currentBets = state.betStack[state.betStack.length - 1];
      const tableLimit = state.tableLimits?.max ?? Infinity;

      const newBet = getNewBet(
        currentBets,
        changedBets,
        decimals,
        state.betLimits ?? {},
        state.betList,
        tableLimit,
        balance,
        freeChipValue,
        freeChipBalance
      );
      if (!newBet) return;

      const totalBet = countTotalBet(newBet.betSpots, decimals);

      const freeBetPlaced = isFreeBetPlaced(newBet.betSpots);
      const availableBalance = freeBetPlaced ? freeChipBalance : balance;
      if (!areBetsEqual(newBet, currentBets) && availableBalance >= totalBet && totalBet <= tableLimit) {
        state.betStack.push(newBet);
        state.updateTimestamp = Date.now();
      }
    },
    clearBet: (state, action: PayloadAction<number>) => {
      if (!state.betList) {
        return;
      }
      const newBets = emptyBets();
      const currentBets = state.betStack[state.betStack.length - 1];
      const defaultBetSpotObj = {
        value: 0,
        count: 0,
        type: ChipTypes.CasinoChip,
      };
      state.betList.forEach((betId) => {
        const betSpot = currentBets?.betSpots?.[betId] ?? defaultBetSpotObj;
        let { value, count } = betSpot;

        if (betId === action.payload) {
          value = count = 0;
        }
        newBets.betSpots[betId] = {
          value: value,
          type: betSpot.type,
        };
        if (betSpot.type === ChipTypes.FreeChip) {
          newBets.betSpots[betId].count = count;
        }
      });

      state.betStack.push(newBets);
      state.updateTimestamp = Date.now();
    },
    popBet: (state) => {
      state.betStack.pop();
      state.updateTimestamp = Date.now();
    },
    wipeBet: (state, action: PayloadAction<number>) => {
      if (!state.betList && !state.betStack.length) {
        return;
      }
      state.betStack.forEach((bet) => {
        if (bet.betSpots[action.payload]) {
          bet.betSpots[action.payload].value = 0;
        }
      });
      if (state.lastBet) {
        const newLastBets = { ...state.lastBet.betSpots };
        if (newLastBets[action.payload]) {
          newLastBets[action.payload].value = 0;
        }
        if (Object.keys(newLastBets).some((betSpot) => newLastBets[betSpot]?.value)) {
          state.lastBet.betSpots = newLastBets;
        } else {
          state.lastBet = null;
        }
      }
    },
    doubleBet: (
      state,
      action: PayloadAction<{ balance: number; decimals: number; freeChipValue: number; freeChipBalance: number }>
    ) => {
      const { balance, decimals, freeChipValue, freeChipBalance } = action.payload;
      const currentBets = state.betStack[state.betStack.length - 1];
      const betLimits = state.betLimits ?? {};
      const tableLimit = state.tableLimits?.max ?? Infinity;
      if (currentBets && state.betList) {
        const newBets = doubleBets(
          balance,
          decimals,
          currentBets,
          betLimits,
          tableLimit,
          state.totalBet,
          freeChipValue,
          freeChipBalance
        );
        const totalBet = newBets ? countTotalBet(newBets.betSpots, decimals) : 0;

        if (
          newBets &&
          !areBetsEqual(newBets, currentBets) &&
          Object.keys(newBets.betSpots).length > 0 &&
          totalBet <= tableLimit
        ) {
          state.betStack.push(newBets);
          state.updateTimestamp = Date.now();
        }
      }
    },
    saveLastBets: (state, action: PayloadAction<{ tableId: string }>) => {
      const currentBets = state.betStack[state.betStack.length - 1];
      if (currentBets && state.totalBet > 0) {
        state.lastBet = currentBets;
        CoreSessionStorage.setLastBets(currentBets, action.payload.tableId);
      }
    },
    clearLastBets: (state) => {
      state.lastBet = null;
    },
    initLastBets: (state, action: PayloadAction<{ bets: BetType }>) => {
      // Use only on game init
      state.lastBet = action.payload.bets;
    },
    reBet: (state, action: PayloadAction<{ balance: number; decimals: number }>) => {
      if (state.lastBet) {
        const { balance, decimals } = action.payload;
        const totalBet = countTotalBet(state.lastBet.betSpots, decimals);

        if (totalBet <= balance) {
          state.betStack.push(state.lastBet);
          state.updateTimestamp = Date.now();
          state.rebetAttempts += 1;
        }
      }
    },
    setAcceptedBets: (state, action: PayloadAction<AcceptedBet[]>) => {
      state.acceptedBets = action.payload;
    },
    pushAcceptedBet: (state, action: PayloadAction<AcceptedBet>) => {
      if (!acceptedBetAlreadyExists(state.acceptedBets, action.payload)) {
        state.acceptedBets.push(action.payload);
      }
    },
    updateAcceptedBet: (state, action: PayloadAction<AcceptedBet>) => {
      const betToUpdate = state.acceptedBets.find((bet) => bet.betCode === action.payload.betCode);
      if (betToUpdate) {
        Object.assign(betToUpdate, action.payload);
        betToUpdate.errorCode = 0;
        betToUpdate.type = action.payload.bettype;
        if (action.payload.bettype === ChipTypes.FreeChip) {
          betToUpdate.count = action.payload.nc;
        }
      }
    },
    setDragBuffer: (state, action: PayloadAction<BetsSliceType['dragBuffer']>) => {
      state.dragBuffer = action.payload ? { ...action.payload, preliminary: true } : null;
    },
    setDisabledBetSpots: (state, action: PayloadAction<string[]>) => {
      state.disabledBetSpots = action.payload;
    },
    promoteDragBuffer: (state) => {
      if (state.dragBuffer) {
        state.dragBuffer.preliminary = false;
      }
    },
    setRawTotalBet: (state, action: PayloadAction<number>) => {
      state.rawTotalBet = action.payload;
      state.totalBet = BetOverrider.totalBetOverrider(state.rawTotalBet);
    },
    recalculateTotalBet: (state) => {
      state.totalBet = BetOverrider.totalBetOverrider(state.rawTotalBet);
    },
    setPlacedBetType: (state, action: PayloadAction<string>) => {
      state.placedBetType = action.payload;
    },
    setMultiplierFactor: (state, action: PayloadAction<number>) => {
      state.multiplierFactor = action.payload;
    },
    resetMultiplierFactor: (state) => {
      state.multiplierFactor = null;
    },
  },
});

export const pushAutoplayBet = createAction<{
  bet: BetType;
  balance: number;
  decimals: number;
  freeChipValue: number;
  freeChipBalance: number;
}>('pushAutoplayBet');

export const betActions = betSlice.actions;
